import React, { useState, useMemo } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  Modal, Icon, Button, Input, Header, Form, TextArea, Message,
  Divider,
} from 'semantic-ui-react';
import CopyLinkButton from '../CopyLinkButton';
import { SEND_PROJECT_LANDING_INVITE } from '../../queries/project';
import { USER_BY_EMAIL } from '../../queries/profile';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialForm = {
  email: '',
  firstName: '',
  lastName: '',
};

const EmailPreview = ({ project, form, note }) => {
  const trimmedNote = note.trim();
  return (
    <div className="email-preview">
      <div className="email-preview-body">
        <p>{`Dear ${form.firstName || 'expert'},`}</p>
        <p>
          You&apos;ve been invited to take part in a consulting project on
          {' '}
          Dexter:
          {' '}
          <strong>{project.title}</strong>
          .
        </p>
        <p>
          Dexter is an expert network for clinical and scientific interviews.
          If you accept, Dexter guides you through the project details,
          screening, and scheduling, and handles payment.
        </p>
        <div className={`email-preview-note${trimmedNote ? '' : ' is-empty'}`}>
          <span className="note-label">
            A note from the person who invited you
          </span>
          <p className="note-content">
            {trimmedNote || 'Your note will appear here…'}
          </p>
        </div>
        <div className="email-preview-cta">Open your invitation</div>
        <p>- Your Dexter Team</p>
      </div>
    </div>
  );
};

const ProjectShareModal = ({ children, project }) => {
  const projectLink = `${process.env.REACT_APP_EXPERT_APP_URL}/landing/projects/${project.id}`;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [note, setNote] = useState('');
  const [existingUser, setExistingUser] = useState(null);
  const [successEmail, setSuccessEmail] = useState('');
  const [error, setError] = useState(null);

  const [lookupUser, { loading: lookupLoading }] = useLazyQuery(USER_BY_EMAIL, {
    fetchPolicy: 'network-only',
  });
  const [sendProjectLandingInvite, { loading }] = useMutation(SEND_PROJECT_LANDING_INVITE);

  const isValidEmail = useMemo(() => EMAIL_REGEX.test(form.email.trim()), [form.email]);
  const canSubmit = useMemo(
    () => form.firstName.trim() && form.lastName.trim(),
    [form],
  );

  const updateForm = (field) => (_event, { value }) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSuccessEmail('');
    setError(null);
  };

  const updateNote = (_event, { value }) => {
    setNote(value);
    setSuccessEmail('');
    setError(null);
  };

  const resetInviteForm = () => {
    setStep(1);
    setForm(initialForm);
    setNote('');
    setExistingUser(null);
    setSuccessEmail('');
    setError(null);
  };

  const goToDetailsStep = async () => {
    if (!isValidEmail || lookupLoading) return;

    setError(null);
    try {
      const { data } = await lookupUser({ variables: { email: form.email.trim() } });
      const matchedUser = data?.userByEmail;
      if (matchedUser) {
        setExistingUser(matchedUser);
        setForm((current) => ({
          ...current,
          firstName: matchedUser.firstName || '',
          lastName: matchedUser.lastName || '',
        }));
      } else {
        setExistingUser(null);
      }
      if (matchedUser?.jobs?.some((job) => job.project.id === project.id)) {
        setError('This person already has a Dexter expert profile and has been invited to this project. Try inviting someone else.');
        return;
      }
      setStep(2);
    } catch (err) {
      setError(err.message || 'Could not look up that email. Please try again.');
    }
  };

  const goBackToEmailStep = () => {
    setStep(1);
    setSuccessEmail('');
    setError(null);
  };

  const submitInvite = async () => {
    if (!canSubmit || loading) return;

    setSuccessEmail('');
    setError(null);
    try {
      const invitedEmail = form.email.trim();
      const trimmedNote = note.trim();
      await sendProjectLandingInvite({
        variables: {
          input: {
            projectId: project.id,
            email: invitedEmail,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            ...(trimmedNote ? { message: trimmedNote } : {}),
          },
        },
      });
      setSuccessEmail(invitedEmail);
      setStep(1);
      setForm(initialForm);
      setNote('');
      setExistingUser(null);
    } catch (err) {
      setError(err.message || 'Failed to send invitation. Please try again.');
    }
  };

  return (
    <Modal
      closeIcon
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        resetInviteForm();
      }}
      trigger={children}
      id="project-share-modal"
    >
      <Modal.Header>Do you know someone who is a good fit?</Modal.Header>
      <Modal.Content>
        <p>
          Share the project link or send a personal invitation. Dexter will
          handle screening, scheduling and payments.
        </p>
        <div className="project-link-section">
          <Header>
            <Icon name="linkify" />
            <Header.Content>Directly share the project link</Header.Content>
          </Header>
          <div className="copy-section">
            <span className="form-label">Project link</span>
            <Input
              fluid
              label={(
                <CopyLinkButton
                  content="Copy"
                  className="basic-btn"
                  copyText={projectLink}
                />
              )}
              labelPosition="right"
              readOnly
              value={projectLink}
              className="form-input"
            />
          </div>
        </div>
        <Divider horizontal>Or</Divider>
        <div className="message-template-section">
          <Header>
            <Icon name="mail" />
            <Header.Content>Send a personal invitation email</Header.Content>
          </Header>
          {!successEmail && (
            <div className="invite-steps">
              <span className={`step${step === 1 ? ' active' : ''}`}>1. Email</span>
              <Icon name="chevron right" size="small" />
              <span className={`step${step === 2 ? ' active' : ''}`}>2. Details</span>
            </div>
          )}

          {step === 1 && successEmail && (
            <div>
              <Message success>
                <Message.Header>{`Invitation sent to ${successEmail}.`}</Message.Header>
                <p>{`Sent by Dexter with the subject "Dexter consulting invitation: ${project.title}"`}</p>
                <p>
                  The expert will receive a secure link from Dexter to review the project.
                  You&apos;ll also receive a copy of this email (without the link) as confirmation.
                </p>
              </Message>
              <Button className="primary-btn" type="button" onClick={resetInviteForm}>
                Invite someone else
              </Button>
            </div>
          )}

          {step === 1 && !successEmail && (
            <Form error={!!error} onSubmit={goToDetailsStep}>
              <Form.Input
                required
                autoFocus
                label="Who would you like to invite?"
                placeholder="name@example.com"
                type="email"
                value={form.email}
                onChange={updateForm('email')}
              />
              {error && <Message error content={error} />}
              <Button
                className="primary-btn"
                loading={lookupLoading}
                disabled={!isValidEmail || lookupLoading}
                type="submit"
              >
                Next
              </Button>
            </Form>
          )}

          {step === 2 && (
            <Form error={!!error} onSubmit={submitInvite}>
              {existingUser ? (
                <Message
                  info
                  icon="user"
                  header={`This person (${form.email}) is already on Dexter`}
                  content="We pre-filled their name for you. Add a note below and send your invitation."
                />
              ) : (
                <Message
                  info
                  icon="user plus"
                  content={`This person (${form.email}) is new to Dexter. Add their name and an optional note below.`}
                />
              )}
              <Message info>
                Dexter sends this invitation form from
                {' '}
                <strong>info@dexterexperts.com</strong>
                {' '}
                in Dexter&apos;s own voice.
                Your name won&apos;t appear in the email -
                unless you include it in the optional note below.
                Whatever you add to the note, Dexter includes in the invitation.
              </Message>
              <Form.Group widths="equal">
                <Form.Input
                  required
                  label="First name"
                  value={form.firstName}
                  disabled={!!existingUser?.firstName}
                  onChange={updateForm('firstName')}
                />
                <Form.Input
                  required
                  label="Last name"
                  value={form.lastName}
                  disabled={!!existingUser?.lastName}
                  onChange={updateForm('lastName')}
                />
              </Form.Group>
              <div className="email-preview-section">
                <div className="email-preview-frame">
                  <div className="email-preview-caption">
                    <span className="caption-label">
                      <Icon name="eye" />
                      Email preview
                    </span>
                    <span className="preview-badge">Preview only</span>
                  </div>
                  <EmailPreview project={project} form={form} note={note} />
                </div>
              </div>
              <Form.Field
                control={TextArea}
                label="Add a note (optional)"
                placeholder="Add a personal note — it will appear in the highlighted box above."
                value={note}
                onChange={updateNote}
                rows={4}
              />
              <Message error content={error} />
              <div className="step-actions">
                <Button
                  type="button"
                  className="basic-btn"
                  onClick={goBackToEmailStep}
                >
                  Back
                </Button>
                <Button
                  className="primary-btn"
                  loading={loading}
                  disabled={!canSubmit || loading}
                  type="submit"
                >
                  Send invitation
                </Button>
              </div>
            </Form>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button className="basic-btn" onClick={() => setOpen(false)}>Done</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ProjectShareModal;
